/**
 * 69.32 (the owner, Oct 23: "I was thinking drone would only drop of supplies for help territory, for instance actually dropping
 * the supply crate for a captured point rather than it already being there"; 268 (5): "drone count will be potentially variable
 * like other vehicles"; 270 (5): "landing is actually safter as it limits aa exposure, so probably bias towards quick hot drop
 * landing for drone rather than winch"; GameConfig.droneDrops): the supply drone.
 * - **A side's drones are a stock** (`supplyDrones.stock`, a count like its vehicles'); a drone on a run is out of it till it is
 *   back at its source and reloaded (SUPPLY_DRONE.reloadSeconds).
 * - **A point taken gets its crate by drone:** the points stand bare at the start; once a side holds one (taken, nobody
 *   capturing) with no crate there, or its crate run dry, the side's nearest stocked crate — its base's or a FOB's — sends a
 *   drone with a drop crate's load (DROP_CRATE.contents), the load off that crate's count. No 30 s refill (core/supply.ts
 *   refillPointCrates is off with the switch).
 * - **The run:** up from its source, along the short great circle — bent round the cover its side knows of as a dropship's
 *   (core/skyRoute.ts; the sky rule's `refuse` refuses a run that would bend, `fly` flies straight) — at SUPPLY_DRONE.speedPeople,
 *   down quick at the point (down, crate off, up: SUPPLY_DRONE.groundSeconds), and back. A landing under known cover is not flown.
 * - **Step 2, the call** (the `supplyDrop` input: the radial's, a commander's for a squad running low): a drone onto ground the
 *   side holds or contests (not a point another side holds unchallenged) with small arms (a drop crate's load) or one typed
 *   ammunition crate (SUPPLY_DRONE.ammo: rockets, mortar bombs — with munitionsSupply, off the dump's rounds); the crate set
 *   down there is the side's.
 * Pure: the Match steps it; the times are fixed at the launch (closed form), so its pose is a function of the tick.
 */
import type { AmmoType, DroneRun, GameState, StructureState, SupplyContents, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, PHYS_DT, DROP_CRATE, SUPPLY_DRONE } from '../constants';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { personSetOf } from './scaleProfile';
import { standingWithJob, structureHealth } from './body';
import { pointCrateDir } from './supply';
import { coverDiscs, pilotRisk, routeAround, skyRuleOf } from './skyRoute';
import { battlePlanetIndex } from './actionSetup';
import { personPointOn } from './fineTerrain';

const ticksOf = (seconds: number): number => Math.max(1, Math.round(seconds / PHYS_DT));
const personOf = (state: Pick<GameState, 'config'>): number => PERSON_BODY_HEIGHT * personSetOf(state).k;
const arc = (a: Vec3, b: Vec3, R: number): number => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * R;

/** Each side's drones at the match's start (`byTeam[t]`, SUPPLY_DRONE.stock where unset), no runs. */
export function withSupplyDrones(state: GameState, byTeam: readonly number[] | undefined): GameState {
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const stock: Record<number, number> = {};
  for (const t of teams) stock[t] = Math.max(0, Math.floor(byTeam?.[t] ?? SUPPLY_DRONE.stock));
  return { ...state, supplyDrones: { stock, runs: [], next: 0 } };
}

/** The side's drones free at `tick`: its stock less those on a run (out, or back and not yet reloaded). */
export function dronesFree(state: Pick<GameState, 'supplyDrones'>, team: number, tick: number): number {
  const d = state.supplyDrones;
  if (!d) return 0;
  return (d.stock[team] ?? 0) - d.runs.filter(r => r.team === team && tick < r.readyTick).length;
}

/** A run's route, its source to its landing (the via points between). */
const routeOf = (r: Pick<DroneRun, 'from' | 'to' | 'via'>): Vec3[] => [r.from, ...(r.via ?? []), r.to];
function routeLength(pts: readonly Vec3[], R: number): number {
  let L = 0;
  for (let i = 1; i < pts.length; i++) L += arc(pts[i - 1], pts[i], R);
  return L;
}
/** The point `dist` along the route (world units), and the way it heads there. */
function routeAt(pts: readonly Vec3[], R: number, dist: number): { dir: Vec3; heading: Vec3 } {
  let left = Math.max(0, dist);
  for (let i = 1; i < pts.length; i++) {
    const a = vecNormalize(pts[i - 1]), b = vecNormalize(pts[i]), L = arc(a, b, R);
    if (left <= L || i === pts.length - 1) {
      const raw = vecSub(b, vecScale(a, vecDot(a, b))), t = vecLength(raw) > 1e-12 ? vecNormalize(raw) : anyTangent(a);
      const th = Math.min(left, L) / R, dir = vecNormalize(vecAdd(vecScale(a, dcos(th)), vecScale(t, dsin(th))));
      const head = vecSub(vecScale(t, dcos(th)), vecScale(a, dsin(th)));
      return { dir, heading: vecLength(head) > 1e-12 ? vecNormalize(head) : t };
    }
    left -= L;
  }
  return { dir: vecNormalize(pts[pts.length - 1]), heading: anyTangent(pts[pts.length - 1]) };
}
/** A unit tangent at `d` (a heading where the route gives none: a run to its own source's spot). */
function anyTangent(d: Vec3): Vec3 {
  const n = vecNormalize(d), ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  return vecNormalize(vecSub(ref, vecScale(n, vecDot(n, ref))));
}

/**
 * A run of `team`'s from crate `source` to `to` at `tick`, or null — the landing under cover its side knows of, or (the sky rule
 * `refuse`) a way in that would bend round it. Its times from its route's length at SUPPLY_DRONE.speedPeople.
 */
export function planDroneRun(state: GameState, team: number, source: StructureState, to: Vec3, tick: number, id: number, load: SupplyContents, site?: number, ammo?: Partial<Record<AmmoType, number>>): DroneRun | null {
  const pi = source.planetIndex, planet = state.planets[pi];
  if (!planet) return null;
  const from = vecNormalize(source.surfaceDir), land = vecNormalize(to), rule = skyRuleOf(state.config);
  const discs = rule === 'fly' ? [] : coverDiscs(state, team, pi, pilotRisk(state.config, team));
  if (discs.some(k => dacos(Math.max(-1, Math.min(1, vecDot(k.c, land)))) <= k.rho)) return null;   // (the landing under their guns)
  const via = discs.length ? routeAround(from, land, discs, planet.radius).via : [];
  if (via.length && rule === 'refuse') return null;
  const L = routeLength([from, ...via, land], planet.radius), speed = SUPPLY_DRONE.speedPeople * personOf(state);
  const leg = ticksOf(2 * SUPPLY_DRONE.climbSeconds + L / speed);
  const downTick = tick + leg, upTick = downTick + ticksOf(SUPPLY_DRONE.groundSeconds), homeTick = upTick + leg;
  return { id, team, planetIndex: pi, sourceId: source.id, from, to: land, ...(via.length ? { via } : {}), ...(site !== undefined ? { site } : {}), load, ...(ammo ? { ammo } : {}), launchTick: tick, downTick, upTick, homeTick, readyTick: homeTick + ticksOf(SUPPLY_DRONE.reloadSeconds) };
}

/** A run's pose at `tick` (null on the ground at its source, before or after): where it is over the ground, how high (world units), which way it heads, and whether it carries its load. */
export function droneRunPose(state: Pick<GameState, 'planets' | 'config'>, r: DroneRun, tick: number): { dir: Vec3; height: number; heading: Vec3; carrying: boolean } | null {
  if (tick < r.launchTick || tick >= r.homeTick) return null;
  const R = state.planets[r.planetIndex].radius, pts = routeOf(r), L = routeLength(pts, R);
  const top = SUPPLY_DRONE.heightPeople * personOf(state), climb = ticksOf(SUPPLY_DRONE.climbSeconds);
  const out = tick < r.upTick, t0 = out ? r.launchTick : r.upTick, t1 = out ? r.downTick : r.homeTick;
  if (out && tick >= r.downTick) { const at = routeAt(pts, R, L); return { dir: vecNormalize(r.to), height: 0, heading: at.heading, carrying: false }; }
  const span = Math.max(1, t1 - t0 - 2 * climb), u = Math.max(0, Math.min(1, (tick - t0 - climb) / span));
  const at = routeAt(pts, R, (out ? u : 1 - u) * L);
  const h = Math.min(1, (tick - t0) / climb, (t1 - tick) / climb) * top;
  return { dir: at.dir, height: Math.max(0, h), heading: out ? at.heading : vecScale(at.heading, -1), carrying: out };
}

/** Step 3: a supply drone's id among the mounts' ship targets (core/pointDefence.ts; 69.10's lighters are 1 000 000 up). */
export const SUPPLY_DRONE_ID_BASE = 2_000_000;

/** Step 3: the drones a round or a burst can meet at `tick` — each run's in the air or standing at its drop, its body's middle (world). */
export function supplyDroneTargets(state: Pick<GameState, 'planets' | 'config' | 'supplyDrones'>, tick: number): Array<{ run: DroneRun; pos: Vec3 }> {
  const runs = state.supplyDrones?.runs;
  if (!runs?.length) return [];
  const ps = personSetOf(state), mid = (SUPPLY_DRONE.hitPeople / 2) * personOf(state), out: Array<{ run: DroneRun; pos: Vec3 }> = [];
  for (const r of runs) {
    const p = droneRunPose(state, r, tick), planet = state.planets[r.planetIndex];
    if (p && planet) out.push({ run: r, pos: personPointOn(planet, p.dir, ps, p.height + mid) });
  }
  return out;
}

/**
 * Step 3: `amount` of damage to run `runId`'s drone at `tick` — at its health's end it is down: the run over (a load not yet set
 * down lost with it), the drone gone from its side's stock, and a `downed` entry for the Match's event. The same state when
 * there is no such run.
 */
export function hitSupplyDrone(state: GameState, runId: number, amount: number, tick: number): GameState {
  const d = state.supplyDrones, r = d?.runs.find(x => x.id === runId);
  if (!d || !r || !(amount > 0)) return state;
  const health = (r.health ?? SUPPLY_DRONE.health) - amount;
  if (health > 0) return { ...state, supplyDrones: { ...d, runs: d.runs.map(x => (x.id === runId ? { ...x, health } : x)) } };
  const p = droneRunPose(state, r, tick), planet = state.planets[r.planetIndex];
  const at = planet ? personPointOn(planet, p ? p.dir : vecNormalize(r.from), personSetOf(state), p ? p.height : 0) : vecNormalize(r.from);
  return { ...state, supplyDrones: { ...d, runs: d.runs.filter(x => x.id !== runId), stock: { ...d.stock, [r.team]: Math.max(0, (d.stock[r.team] ?? 0) - 1) },
    downed: [...(d.downed ?? []), { id: r.id, team: r.team, planetIndex: r.planetIndex, at, carrying: !!p?.carrying }] } };
}

/** The side's crates a drone can load at: its own standing crates (the base's, its FOBs'; not a point's, not one a drone set down) holding a whole load. */
function sources(state: GameState, team: number, load: SupplyContents, ammo?: Partial<Record<AmmoType, number>>): StructureState[] {
  return state.structures.filter(s => standingWithJob(s, 'supply') && s.supply && s.supply.site === undefined && s.supply.teamIndex === team && !s.supply.dropped
    && s.supply.bandages >= (load.bandages ?? 0) && s.supply.magazines >= (load.magazines ?? 0)
    && Object.entries(ammo ?? {}).every(([k, n]) => (s.supply!.ammo?.[k as AmmoType] ?? 0) >= (n ?? 0)));
}

/** A crate's supply less a load (and its rounds). */
function less(s: StructureState, load: SupplyContents, ammo?: Partial<Record<AmmoType, number>>): StructureState {
  const sup = s.supply!, left = ammo ? Object.fromEntries(Object.entries(sup.ammo ?? {}).map(([k, n]) => [k, (n ?? 0) - (ammo[k as AmmoType] ?? 0)])) : undefined;
  return { ...s, supply: { ...sup, bandages: sup.bandages - (load.bandages ?? 0), magazines: sup.magazines - (load.magazines ?? 0), ...(left ? { ammo: left } : {}) } };
}

/**
 * Step 2: a `supplyDrop` call — a drone of `team`'s onto `at` with `kind`'s load, from its nearest crate holding it; refused with
 * the switch off, off the battle planet, onto a point another side holds (nobody contesting it), with no drone free, rounds
 * asked without munitions supply, no crate of the side's holding the load, or its landing under known cover (or, the sky rule
 * `refuse`, a way in round it).
 */
export function callSupplyDrop(state: GameState, team: number, at: { planetIndex: number; surfaceDir: Vec3 }, kind: 'smallArms' | 'rocket' | 'mortarBomb', tick: number): { state: GameState; run: DroneRun } | { refused: string } {
  const d = state.supplyDrones;
  if (!state.config.droneDrops || !d) return { refused: 'Drone supply drops are off' };
  if (at.planetIndex !== battlePlanetIndex(state) || !state.planets[at.planetIndex]) return { refused: 'Not on the battle planet' };
  const R = state.planets[at.planetIndex].radius, ring = personSetOf(state).captureRadius;
  const theirs = (state.modeState.points?.sites ?? []).find(s => s.planetIndex === at.planetIndex && s.owner !== null && s.owner !== undefined && s.owner !== team && s.capturer === null && arc(s.surfaceDir, at.surfaceDir, R) <= ring);
  if (theirs) return { refused: 'Their ground' };
  if (dronesFree(state, team, tick) <= 0) return { refused: 'No drone free' };
  if (kind !== 'smallArms' && !state.config.munitionsSupply) return { refused: 'No rounds at the dumps' };
  const load: SupplyContents = kind === 'smallArms' ? DROP_CRATE.contents : {}, ammo = kind === 'smallArms' ? undefined : { [kind]: SUPPLY_DRONE.ammo[kind] } as Partial<Record<AmmoType, number>>;
  const from = sources(state, team, load, ammo).filter(s => s.planetIndex === at.planetIndex).sort((a, b) => arc(a.surfaceDir, at.surfaceDir, R) - arc(b.surfaceDir, at.surfaceDir, R) || a.id - b.id)[0];
  if (!from) return { refused: kind === 'smallArms' ? 'No supplies at the dumps' : 'No rounds at the dumps' };
  const run = planDroneRun(state, team, from, at.surfaceDir, tick, d.next, load, undefined, ammo);
  if (!run) return { refused: 'Their guns cover it' };
  return { state: { ...state, structures: state.structures.map(s => (s.id === from.id ? less(s, load, ammo) : s)), supplyDrones: { ...d, runs: [...d.runs, run], next: d.next + 1 } }, run };
}

/**
 * The drones' step at `tick`: a run down now sets its load (a point's crate made at its spot, or the empty one there filled);
 * the runs done drop out (the drone back in the stock); and, once a second, each point a side holds with no crate there or its
 * crate run dry, and no run of the side's on its way to it, gets one — from the side's nearest stocked crate with a drone
 * free, the load off that crate's count. The drops made, for the Match's events. The same state when nothing changes.
 */
export function stepSupplyDrones(state: GameState, tick: number): { state: GameState; drops: Array<{ run: DroneRun; structureId: number }>; launched: DroneRun[] } {
  const d = state.supplyDrones;
  if (!d) return { state, drops: [], launched: [] };
  let structures = state.structures, runs = d.runs, next = d.next, changed = false;
  const drops: Array<{ run: DroneRun; structureId: number }> = [], launched: DroneRun[] = [];
  // the loads down now
  for (const r of runs) {
    if (r.dropped || tick < r.downTick) continue;
    const site = r.site !== undefined ? state.modeState.points?.sites.find(x => x.id === r.site) : undefined;
    const there = r.site !== undefined ? structures.find(s => s.type === 'supply' && s.health > 0 && s.supply?.site === r.site) : undefined;
    let id: number;
    if (there) {
      id = there.id;
      structures = structures.map(s => (s.id === there.id ? { ...s, supply: { ...s.supply!, bandages: Math.max(s.supply!.bandages, r.load.bandages ?? 0), magazines: Math.max(s.supply!.magazines, r.load.magazines ?? 0) } } : s));
    } else {
      id = structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
      const dir = site ? pointCrateDir(state, site) : r.to;
      structures = [...structures, { id, type: 'supply', planetIndex: r.planetIndex, surfaceDir: dir, health: structureHealth('supply'), ownerId: -1,
        supply: { teamIndex: r.site !== undefined ? -1 : r.team, ...(r.site !== undefined ? { site: r.site } : { dropped: true as const }), bandages: r.load.bandages ?? 0, magazines: r.load.magazines ?? 0, ...(r.ammo ? { ammo: { ...r.ammo } } : {}) } }];
    }
    runs = runs.map(x => (x.id === r.id ? { ...x, dropped: true as const } : x));
    drops.push({ run: r, structureId: id });
    changed = true;
  }
  // the runs done: the drone back in the stock
  if (runs.some(r => tick >= r.readyTick)) { runs = runs.filter(r => tick < r.readyTick); changed = true; }
  // once a second: the points held with no crate there (or it run dry) and no run on its way
  if (tick % ticksOf(1) === 0) {
    const sites = [...(state.modeState.points?.sites ?? [])].sort((a, b) => a.id - b.id);
    for (const site of sites) {
      const team = site.owner;
      if (team === null || team === undefined || site.capturer !== null) continue;
      const crate = structures.find(s => s.type === 'supply' && s.health > 0 && s.supply?.site === site.id);
      if (crate && (crate.supply!.bandages > 0 || crate.supply!.magazines > 0)) continue;
      if (runs.some(r => r.team === team && r.site === site.id && !r.dropped)) continue;
      if (dronesFree({ supplyDrones: { ...d, runs } }, team, tick) <= 0) continue;
      const load = DROP_CRATE.contents, R = state.planets[site.planetIndex]?.radius ?? 1;
      const from = sources({ ...state, structures }, team, load).filter(s => s.planetIndex === site.planetIndex)
        .sort((a, b) => arc(a.surfaceDir, site.surfaceDir, R) - arc(b.surfaceDir, site.surfaceDir, R) || a.id - b.id)[0];
      if (!from) continue;
      const run = planDroneRun({ ...state, structures }, team, from, pointCrateDir(state, site), tick, next, load, site.id);
      if (!run) continue;
      next++;
      runs = [...runs, run];
      structures = structures.map(s => (s.id === from.id ? less(s, load) : s));
      launched.push(run);
      changed = true;
    }
  }
  if (!changed) return { state, drops, launched };
  return { state: { ...state, structures, supplyDrones: { ...d, runs, next } }, drops, launched };
}
